import {ChangeDetectionStrategy, Component, computed, effect, inject} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {Checkbox} from 'primeng/checkbox';
import {lastValueFrom} from 'rxjs';
import {DashboardService, DashboardUrgentTodo} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {QueryKey} from '../../../../constants/query-key';
import {RouteUri} from '../../../../constants/route-uri';
import {DueDateClassPipe} from '../../../todos/pipes/due-date-class.pipe';
import {DueDateFormatPipe} from '../../../todos/pipes/due-date-format.pipe';
import {DueDateFullPipe} from '../../../todos/pipes/due-date-full.pipe';
import {PendingCompletionService} from '../../services/pending-completion.service';
import {DashboardTodoPriorityChart} from '../dashboard-todo-priority-chart/dashboard-todo-priority-chart';

type UrgencyGroup = {
  labelKey: string;
  todos: DashboardUrgentTodo[];
};

@Component({
  selector: 'elcia-dashboard-urgent-todos',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Checkbox,
    DashboardTodoPriorityChart,
    ElciaCard,
    DueDateClassPipe,
    DueDateFormatPipe,
    DueDateFullPipe,
    ElciaButton,
    Loader,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
  ],
  providers: [PendingCompletionService],
  templateUrl: './dashboard-urgent-todos.html',
  host: {class: 'block min-w-0'},
})
export class DashboardUrgentTodos {
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  protected readonly pending = inject(PendingCompletionService);

  protected readonly todosLink = '/' + RouteUri.TODOS;

  private readonly statsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_TODO_STATS],
    queryFn: () => lastValueFrom(this.dashboardService.getDashboardTodoStats()),
  }));

  protected readonly isLoading = computed(() => this.statsQuery.isPending());
  protected readonly todos = computed(() => this.statsQuery.data()?.stats?.urgentTodos ?? []);
  protected readonly pendingCount = computed(() => this.statsQuery.data()?.stats?.pendingCount ?? 0);

  private readonly syncControls = effect(() => this.pending.syncControls(this.todos()));

  // Grouping happens here rather than server-side: the backend already sorts by urgency, and the
  // boundaries (today, end of week) belong to the reader's timezone, not the server's.
  protected readonly groups = computed<UrgencyGroup[]>(() => {
    const overdue: DashboardUrgentTodo[] = [];
    const thisWeek: DashboardUrgentTodo[] = [];
    const later: DashboardUrgentTodo[] = [];

    for (const todo of this.todos()) {
      const days = this.daysUntilDue(todo.dueDate);
      if (days === null || days > 7) later.push(todo);
      else if (days < 0) overdue.push(todo);
      else thisWeek.push(todo);
    }

    return [
      {labelKey: 'dashboard.todos.overdue-group', todos: overdue},
      {labelKey: 'dashboard.todos.this-week-group', todos: thisWeek},
      {labelKey: 'dashboard.todos.later-group', todos: later},
    ].filter(group => group.todos.length > 0);
  });

  protected openTodo(todo: DashboardUrgentTodo): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([this.todosLink], {
        queryParams: todo.parentId != null ? {highlightChildId: todo.id} : {highlightTodoId: todo.id},
      }),
    );
    window.open(url, '_blank');
  }

  private daysUntilDue(dueDate: Date | null | undefined): number | null {
    if (!dueDate) return null;

    const due = new Date(dueDate);
    const today = new Date();
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }
}
